import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  addActiveTab,
  removeActiveTab,
  resetActiveTabs,
  rehydrateActiveTabs,
} from "../../reducer/sideBarSlice";
import { toggleDropdown } from "../../reducer/dropdownSlice";
import { Paths } from "../../router/Paths";
import { RootState } from "../../app/store";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

interface Choice {
  name: string;
  path: string;
  subChoices?: { name: string; path: string }[];
}

interface TourManagementSidebarProps {
  selected?: number;
}

const choices: Choice[] = [
  {
    name: "Package category",
    path: Paths.tourManagementCreate,
  },
  {
    name: "Main information",
    path: Paths.tourManagementPackageName,
    subChoices: [
      { name: "Title", path: Paths.tourManagementPackageName },
      {
        name: "Descriptions & highlights",
        path: Paths.tourManagementPackageDescription,
      },
      { name: "Locations", path: Paths.tourManagementPackageLocation },
      { name: "Keywords", path: Paths.tourManagementPackageKeywords },
    ],
  },
  {
    name: "Inclusions",
    path: Paths.tourManagementPackageGuideInfo,
    subChoices: [
      { name: "What's Included", path: Paths.tourManagementPackageInclusion },
      { name: "Guide Information", path: Paths.tourManagementPackageGuideInfo },
      { name: "Food", path: Paths.tourManagementPackageFood },
      {
        name: "Transportation",
        path: Paths.tourManagementPackageTransportation,
      },
      { name: "Extra Information", path: Paths.tourManagementPackageExtraInfo },
      { name: "Photos", path: Paths.tourManagementPackagePhotoUpload },
      { name: "Options", path: Paths.tourManagementPackageOptions },
    ],
  },
  // {
  //   name: "Review",
  //   path: Paths.tourManagementPackageOptions,
  // },
];

const TourManagementSidebar: React.FC<TourManagementSidebarProps> = ({
  selected,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const dropdownStates = useSelector((state: RootState) => state.dropdown);
  const activeTabs = useSelector(
    (state: RootState) => state.sidbarActiveTab.activeTabs
  );

  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    dispatch(rehydrateActiveTabs());
  }, [dispatch]);

  // const handleNavigation = (path: string, isSubChoice = false) => {
  //   if (
  //     isSubChoice &&
  //     !completedSteps.includes(path) &&
  //     location.pathname !== path
  //   ) {

  //     return;
  //   }
  //   if (!activeTabs.includes(path)) {
  //     dispatch(addActiveTab(path));
  //   }
  //   navigate(path);
  // };

  const handleNavigation = (path: string, isSubChoice = false) => {
    // Add the path to active tabs if not already present
    if (!activeTabs.includes(path)) {
      dispatch(addActiveTab(path));
    }

    navigate(path);
  };

  const handleRemoveTab = (path: string) => {
    dispatch(removeActiveTab(path));
  };

  const handleToggleDropdown = (index: number) => {
    dispatch(toggleDropdown(index));
  };

  const isSubChoiceActive = (subChoicePath: string) =>
    location.pathname === subChoicePath;

  const isMainChoiceActive = (mainChoicePath: string) =>
    location.pathname === mainChoicePath ||
    choices
      .find((choice) => choice.path === mainChoicePath)
      ?.subChoices?.some((subChoice) => isSubChoiceActive(subChoice.path));

  const activeChoice =
    choices
      .flatMap((choice) => choice.subChoices || [])
      .find((sub) => sub.path === location.pathname)?.name ||
    choices.find((choice) => isMainChoiceActive(choice.path))?.name ||
    "Select Section";

  return (
    <>
      {/*  Mobile dropdown header */}
      <div className="flex lg:hidden justify-between items-center p-3 bg-blue-500/20 rounded-t-md mt-6">
        <span className="font-bold text-black">{activeChoice}</span>
        <button onClick={() => setIsOpen(!isOpen)} className="text-gray-700">
          {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </button>
      </div>

      {/* Sidebar */}
      <div
        className={`${Classname.container} ${
          !isOpen && "hidden lg:flex"
        } flex-col`}
      >
        {choices.map((choice, index) => (
          <div key={index} className="flex flex-col gap-2">
            {/* Main Choice */}
            <div
              className={`flex gap-3 items-center cursor-pointer ${
                isMainChoiceActive(choice.path)
                  ? "font-bold text-black"
                  : "font-bold text-gray-500"
              }`}
              onClick={() =>
                choice.subChoices
                  ? handleToggleDropdown(index)
                  : handleNavigation(choice.path)
              }
            >
              <div
                className={`flex w-1 h-12 ${
                  isMainChoiceActive(choice.path)
                    ? "bg-primary1"
                    : "bg-transparent"
                }`}
              ></div>
              <span>{choice.name}</span>
              {choice.subChoices && (
                <span className="ml-auto">
                  {dropdownStates[index] ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )}
                </span>
              )}
            </div>

            {/* Sub-choices */}
            {choice.subChoices && dropdownStates[index] && (
              <div className="ml-6">
                {choice.subChoices.map((subChoice, subIndex) => (
                  <div
                    key={subIndex}
                    className={`flex gap-3 items-center cursor-pointer ${
                      activeTabs.includes(subChoice.path)
                        ? "font-bold text-black"
                        : "font-bold text-gray-500"
                    }`}
                    onClick={() => handleNavigation(subChoice.path, true)}
                  >
                    <div
                      className={`flex w-1 h-8 ${
                        isSubChoiceActive(subChoice.path)
                          ? "bg-primary1"
                          : "bg-transparent"
                      }`}
                    ></div>
                    <span>{subChoice.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default TourManagementSidebar;

const Classname = {
  container:
    "flex flex-col w-full lg:max-w-80 bg-blue-500/20 px-4 lg:px-6 lg:py-6",
};
